import React from 'react';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import withStyles, { WithStyles } from '@material-ui/core/styles/withStyles';
import { Theme } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import createStyles from '@material-ui/core/styles/createStyles';
import Menu from '@material-ui/core/Menu';
import ProbabilityInput from './ProbabilityInput';
import StateRefInput from './StateRefInput';
import DistributionInput from './DistributionInput';
import PartsInput from './PartsInput';
import ProxyTypes, {
  DistributionTimeDependence,
  ProxyTypeDisplayNames,
} from '../../../../../mxGraph/HCLTreeNode/HCLTreeVertexValue/ProxyTypes';
import Expression, { ExpressionJSON } from '../../../../../mxGraph/HCLTreeNode/HCLTreeVertexValue/Expression';
import StringInput from './StringInput';
import InputRules from './InputRules';

const styles = (theme: Theme) => createStyles({
});

interface Props extends WithStyles<typeof styles> {
  expression: Expression;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>, name: string, multiple?: boolean) => void;
  onError: (error: boolean) => void;
  name: string;
  theme?: Theme;
  disabled?: boolean;
  override?: {
    type: ProxyTypes;
    eventType?: DistributionTimeDependence;
    distributionType?: ProxyTypes.NORMAL_DISTRIBUTION | ProxyTypes.LOG_NORMAL_DISTRIBUTION | ProxyTypes.UNIFORM_DISTRIBUTION | ProxyTypes.WEIBULL_DISTRIBUTION | ProxyTypes.EXPONENTIAL_DISTRIBUTION | ProxyTypes.NON_PARAMETRIC_DISTRIBUTION;
    params?: ['mean', 'std'] | ['error_factor', 'median'] | ['p5', 'p95'];
  };
}

interface State {
  errorDictionary: {[key: string]: boolean};
}

class ExpressionInput extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    const { expression } = this.props;

    this.state = {
      errorDictionary: { [expression.getProxy()]: false },
    };
  }

  private handleTextFieldChange =async  (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>, error?: boolean, multiple?: boolean) => {
    
    const {
      onChange, name, onError, expression,
    } = this.props;
    console.log("THE e inside",e)
    console.log("ON CHANGE INSIDE ",onChange)
    await onChange(e, name, multiple);
    console.log("CHANGE DONE!");
    await this.setState(prevState => ({ errorDictionary: { ...prevState.errorDictionary, [expression.getProxy()]: error } }));
    await onError(error);
  };

  private handleExpressionTypeChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const {
      onChange, name, onError, expression,
    } = this.props;

    if (e.target.value === expression.getProxy()) return;
    onChange(e, name);
    onError(true);
  };

  private handleTimeDependenceChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const {
      onChange, name, onError, expression,
    } = this.props;

    if (e.target.value === expression.getDistributionTimeDependence()) return;
    expression.setProxy(ProxyTypes.DISTRIBUTION);
    expression.setDistributionTimeDependence(e.target.value as DistributionTimeDependence);
    // @ts-ignore
    e.target.value = expression.toExtendedJSON();
    onChange(e, name, true);
    onError(true);
  };

  private handleDistributionTypeChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const {
      onChange, name, onError, expression,
    } = this.props;
    const { errorDictionary } = this.state;

    if (e.target.value === expression.getProxy()) return;

    onChange(e, name);

    if (errorDictionary[e.target.value] === null || errorDictionary[e.target.value] === undefined) errorDictionary[e.target.value] = true;

    onError(errorDictionary[e.target.value]);
  };

  private handleTraceSelectFromPlayground = (e: React.MouseEvent<HTMLElement, MouseEvent>, selectedTrace: ExpressionJSON) => {
    const { onChange, onError, name } = this.props;
    e.persist();
    // @ts-ignore
    e.target.value = selectedTrace;
    // @ts-ignore
    onChange(e, name, true);

    this.setState(prevState => ({ errorDictionary: { ...prevState.errorDictionary, [selectedTrace._proxy]: false } }));
    onError(false);
  };

  private handleProbabilityChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const isValueValid = InputRules.isProbabilityValid(e.target.value);
    this.handleTextFieldChange(e, !isValueValid);
  };

  render() {
    const { expression, disabled, override } = this.props;

    const currentProxyType = expression.getProxy();

    const proxyTypes = [
      ProxyTypes.FLOAT,
      ProxyTypes.STRING_DISTRIBUTION,
      ProxyTypes.DISTRIBUTION,
      ProxyTypes.BBN_LINK_EXPRESSION,
      ProxyTypes.PARTS_FIT_EXPRESSION,
    ];

    const distributionProxyTypes = [
      ProxyTypes.EXPONENTIAL_DISTRIBUTION,
      ProxyTypes.LOG_NORMAL_DISTRIBUTION,
      ProxyTypes.WEIBULL_DISTRIBUTION,
      ProxyTypes.NORMAL_DISTRIBUTION,
      ProxyTypes.UNIFORM_DISTRIBUTION,
      ProxyTypes.NON_PARAMETRIC_DISTRIBUTION,
    ];

    return (
      <React.Fragment>
        <Grid item xs={12} sm={3}>
          <TextField
            disabled={disabled || override}
            required
            label="Probability Type"
            name="_proxy"
            value={override ? override.type : distributionProxyTypes.includes(currentProxyType) ? ProxyTypes.DISTRIBUTION : currentProxyType}
            onChange={this.handleExpressionTypeChange}
            margin="normal"
            variant="outlined"
            fullWidth
            select
            SelectProps={{
              MenuProps: {
                disablePortal: true,
              },
            }}
          >
            {
              proxyTypes.map(proxyType => (
                <MenuItem key={proxyType} value={proxyType}>
                  {ProxyTypeDisplayNames[proxyType]}
                </MenuItem>
              ))
            }
          </TextField>
        </Grid>
        {
          currentProxyType === ProxyTypes.FLOAT
          && (
            <Grid item xs={12} sm={6} md={6} lg={4} xl={4}>
              <ProbabilityInput
                label="Probability"
                name="value"
                value={expression.getValue()}
                margin="normal"
                variant="outlined"
                onChange={this.handleProbabilityChange}
              />
            </Grid>
          )
        }
        {
          currentProxyType === ProxyTypes.STRING_DISTRIBUTION
          && (
            <Grid item xs={12} sm={6} md={6} lg={4} xl={4}>
              <StringInput
                userExpression={expression.getUserExpression()}
                onChange={this.handleTextFieldChange}
              />
            </Grid>
          )
        }
        {
          currentProxyType === ProxyTypes.BBN_LINK_EXPRESSION
          && (
            <StateRefInput
              expression={expression}
              onChange={this.handleTextFieldChange}
            />
          )
        }
        {
          currentProxyType === ProxyTypes.PARTS_FIT_EXPRESSION
          && (
            <PartsInput
              expression={expression}
              onChange={this.handleTextFieldChange}
            />
          )
        }
        {
          (currentProxyType === ProxyTypes.DISTRIBUTION || distributionProxyTypes.includes(currentProxyType))
          && (
            <DistributionInput
              expression={expression}
              currentProxyType={currentProxyType}
              onTimeDependenceChange={this.handleTimeDependenceChange}
              onDistributionTypeChange={this.handleDistributionTypeChange}
              onTextFieldChange={this.handleTextFieldChange}
              onTraceSelectFromPlayground={this.handleTraceSelectFromPlayground}
              disabled={disabled}
              override={override}
            />
          )
        }
      </React.Fragment>
    );
  }
}

export default withStyles(styles)(ExpressionInput);
